import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Button, Card, DatePicker, Descriptions, Form, Input, InputNumber, Modal, Select, Space, message } from "antd";
import dayjs from "dayjs";
import { PageHeader } from "../../components/PageHeader";
import { DocumentStatusTag } from "../../components/DocumentStatusTag";
import { ApprovalTimeline } from "../../components/ApprovalTimeline";
import { ProjectLink } from "../../components/ProjectLink";
import { SiteLabel } from "../../components/SiteLabel";
import { useApi } from "../../helpers/useApi";
import { fmtItem } from "../../helpers/format";
import { hasAnyGrant, hasPermission } from "../../helpers/permissions";
import { useUser } from "../../context/UserContext";
import { DT_FORMATS } from "../../helpers/constants";
import type { ServiceRequest, Vehicle, Paginated } from "../../api-models";

const DOC_TYPE_BY_TYPE: Record<string, string> = {
  VEHICLE: "SR_VEHICLE",
  STAFF_TRANSFER: "SR_STAFF_TRANSFER",
  EQUIPMENT_TRANSFER: "SR_EQUIPMENT_TRANSFER",
  TRANSPORT: "SR_TRANSPORT",
  RENTAL: "SR_RENTAL",
};

const MOVEMENT_REF_TYPES = [
  { value: "TransferOrder", label: "Transfer Order" },
  { value: "TransferNote", label: "Transfer Note" },
  { value: "MRN", label: "MRN" },
  { value: "ToolCustodyEvent", label: "Tool Custody Event" },
];

export default function ServiceRequestDetailPage() {
  const { id } = useParams();
  const { user } = useUser();
  const { request, loading } = useApi();

  const [sr, setSr] = useState<ServiceRequest | null>(null);
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [assignOpen, setAssignOpen] = useState(false);
  const [completeOpen, setCompleteOpen] = useState(false);
  const [assignForm] = Form.useForm();
  const [completeForm] = Form.useForm();

  const canWrite = hasPermission(user, "ServiceRequests.Write", sr?.siteId);
  const canAssign = hasAnyGrant(user, "ServiceRequests.Assign");

  async function load() {
    const res = await request<ServiceRequest>({ url: `/service-requests/${id}` });
    setSr(res);
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (sr?.type === "VEHICLE") {
      request<Paginated<Vehicle>>({ url: "/vehicles", params: { pageSize: 500, status: "ACTIVE" } }).then((res) => setVehicles(res.data));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sr?.type]);

  async function act(action: string, data?: Record<string, unknown>) {
    await request({ method: "POST", url: `/service-requests/${id}/${action}`, data });
    message.success("Done.");
    load();
  }

  async function submitCancel() {
    if (!cancelReason.trim()) {
      message.error("A reason is required to cancel a service request.");
      return;
    }
    await act("cancel", { reason: cancelReason });
    setCancelOpen(false);
    setCancelReason("");
  }

  async function submitAssign() {
    const values = await assignForm.validateFields().catch(() => ({}) as Record<string, unknown>);
    await act("assign", values);
    setAssignOpen(false);
  }

  async function submitComplete() {
    const values = await completeForm.validateFields().catch(() => ({}) as Record<string, unknown>);
    const payload: Record<string, unknown> = { ...values };
    if (values.actualReturnAt) payload.actualReturnAt = (values.actualReturnAt as dayjs.Dayjs).toISOString();
    if (values.actualStartAt) payload.actualStartAt = (values.actualStartAt as dayjs.Dayjs).toISOString();
    if (values.actualEndAt) payload.actualEndAt = (values.actualEndAt as dayjs.Dayjs).toISOString();
    await act("complete", payload);
    setCompleteOpen(false);
  }

  if (!sr) return null;

  const docType = DOC_TYPE_BY_TYPE[sr.type];
  const canCancel = ["DRAFT", "PENDING_APPROVAL", "APPROVED", "ASSIGNED", "IN_PROGRESS"].includes(sr.status) && (canWrite || canAssign);

  return (
    <div>
      <PageHeader
        title={sr.docNo}
        subtitle={
          <>
            {sr.type.replaceAll("_", " ")} — <SiteLabel site={sr.site} project={sr.project} fallback={<ProjectLink project={sr.project} fallback="" />} />
          </>
        }
        extra={
          <Space>
            <span data-tour="doc-status">
              <DocumentStatusTag status={sr.status} />
            </span>
            {sr.status === "DRAFT" && canWrite && (
              <Button type="primary" onClick={() => act("submit")} loading={loading} data-tour="sr-submit">
                Submit for approval
              </Button>
            )}
            {sr.status === "APPROVED" && canAssign && (
              <Button type="primary" onClick={() => setAssignOpen(true)} loading={loading} data-tour="sr-assign">
                Assign
              </Button>
            )}
            {sr.status === "ASSIGNED" && canAssign && (
              <Button onClick={() => act("start")} loading={loading} data-tour="sr-start">
                Start
              </Button>
            )}
            {(sr.status === "ASSIGNED" || sr.status === "IN_PROGRESS") && canAssign && (
              <Button type="primary" onClick={() => setCompleteOpen(true)} loading={loading} data-tour="sr-complete">
                Complete
              </Button>
            )}
            {sr.status === "COMPLETED" && canAssign && (
              <Button onClick={() => act("close")} loading={loading} data-tour="sr-close">
                Close
              </Button>
            )}
            {canCancel && (
              <Button danger onClick={() => setCancelOpen(true)} loading={loading}>
                Cancel
              </Button>
            )}
          </Space>
        }
      />

      <Card size="small" style={{ marginBottom: 16 }}>
        <Descriptions column={3} size="small">
          <Descriptions.Item label="Project"><ProjectLink project={sr.project} /></Descriptions.Item>
          <Descriptions.Item label="Site">{sr.site?.name}</Descriptions.Item>
          <Descriptions.Item label="Requesting dept">{sr.requestingDept}</Descriptions.Item>
          <Descriptions.Item label="From location">{sr.fromLocation}</Descriptions.Item>
          <Descriptions.Item label="To location">{sr.toLocation}</Descriptions.Item>
          <Descriptions.Item label="Required">{sr.requiredAt ? dayjs(sr.requiredAt).format(DT_FORMATS.dateTime) : "—"}</Descriptions.Item>
          <Descriptions.Item label="Reason" span={3}>{sr.reason}</Descriptions.Item>
          {sr.remarks && <Descriptions.Item label="Remarks" span={3}>{sr.remarks}</Descriptions.Item>}
          {sr.assignee && <Descriptions.Item label="Assignee">{sr.assignee.name}</Descriptions.Item>}
          {sr.cancelReason && <Descriptions.Item label="Cancel reason" span={3}>{sr.cancelReason}</Descriptions.Item>}
        </Descriptions>
      </Card>

      {sr.vehicleDetail && (
        <Card size="small" title="Vehicle details" style={{ marginBottom: 16 }}>
          <Descriptions column={2} size="small">
            <Descriptions.Item label="Type needed">{sr.vehicleDetail.vehicleTypeNeeded}</Descriptions.Item>
            <Descriptions.Item label="Passenger/load">{sr.vehicleDetail.passengerOrLoad}</Descriptions.Item>
            <Descriptions.Item label="Pickup">{sr.vehicleDetail.pickupAt ? dayjs(sr.vehicleDetail.pickupAt).format(DT_FORMATS.dateTime) : "—"}</Descriptions.Item>
            <Descriptions.Item label="Return">{sr.vehicleDetail.returnAt ? dayjs(sr.vehicleDetail.returnAt).format(DT_FORMATS.dateTime) : "—"}</Descriptions.Item>
            <Descriptions.Item label="Assigned vehicle">{sr.vehicleDetail.assignedVehicle?.registrationNo ?? "—"}</Descriptions.Item>
            <Descriptions.Item label="Driver">{sr.vehicleDetail.driverName || "—"} {sr.vehicleDetail.driverContact}</Descriptions.Item>
            <Descriptions.Item label="Actual return">{sr.vehicleDetail.actualReturnAt ? dayjs(sr.vehicleDetail.actualReturnAt).format(DT_FORMATS.dateTime) : "—"}</Descriptions.Item>
          </Descriptions>
        </Card>
      )}

      {sr.staffTransferDetail && (
        <Card size="small" title="Staff transfer details" style={{ marginBottom: 16 }}>
          <Descriptions column={2} size="small">
            <Descriptions.Item label="From site"><SiteLabel site={sr.staffTransferDetail.fromSite} /></Descriptions.Item>
            <Descriptions.Item label="To site"><SiteLabel site={sr.staffTransferDetail.toSite} /></Descriptions.Item>
            <Descriptions.Item label="Effective date">{dayjs(sr.staffTransferDetail.effectiveDate).format(DT_FORMATS.date)}</Descriptions.Item>
            <Descriptions.Item label="Permanent">{sr.staffTransferDetail.permanent ? "Yes" : "No"}</Descriptions.Item>
            <Descriptions.Item label="Expected duration">{sr.staffTransferDetail.expectedDurationNote || "—"}</Descriptions.Item>
            <Descriptions.Item label="Accommodation notes">{sr.staffTransferDetail.accommodationNotes || "—"}</Descriptions.Item>
            <Descriptions.Item label="Staff" span={2}>
              {(sr.staffTransferDetail.staff ?? []).length === 0 ? (
                "—"
              ) : (
                <Space direction="vertical" size={0}>
                  {(sr.staffTransferDetail.staff ?? []).map((s) => (
                    <span key={s.id}>
                      {s.staff?.staffCode ?? s.staffId} - {s.staff?.name ?? ""}
                      {s.createdAssignmentId ? " (transferred)" : ""}
                    </span>
                  ))}
                </Space>
              )}
            </Descriptions.Item>
          </Descriptions>
        </Card>
      )}

      {sr.equipmentTransferDetail && (
        <Card size="small" title="Equipment transfer details" style={{ marginBottom: 16 }}>
          <Descriptions column={2} size="small">
            <Descriptions.Item label="Item">{fmtItem(sr.equipmentTransferDetail.item)}</Descriptions.Item>
            <Descriptions.Item label="Quantity">{sr.equipmentTransferDetail.qty}</Descriptions.Item>
            <Descriptions.Item label="Asset/serial">{sr.equipmentTransferDetail.assetSerial || "—"}</Descriptions.Item>
            <Descriptions.Item label="Condition at request">{sr.equipmentTransferDetail.conditionAtRequest || "—"}</Descriptions.Item>
            <Descriptions.Item label="Movement reference" span={2}>
              {sr.equipmentTransferDetail.movementRefType
                ? `${sr.equipmentTransferDetail.movementRefType} #${sr.equipmentTransferDetail.movementRefId}`
                : "Not yet recorded"}
            </Descriptions.Item>
          </Descriptions>
        </Card>
      )}

      {sr.transportDetail && (
        <Card size="small" title="Transport details" style={{ marginBottom: 16 }}>
          <Descriptions column={2} size="small">
            <Descriptions.Item label="Destination site"><SiteLabel site={sr.transportDetail.destinationSite} /></Descriptions.Item>
            <Descriptions.Item label="Origin">{sr.transportDetail.origin || "—"}</Descriptions.Item>
            <Descriptions.Item label="Transported" span={2}>{sr.transportDetail.transportedDescription}</Descriptions.Item>
          </Descriptions>
        </Card>
      )}

      {sr.rentalDetail && (
        <Card size="small" title="Rental details" style={{ marginBottom: 16 }}>
          <Descriptions column={2} size="small">
            <Descriptions.Item label="Vehicle type">{sr.rentalDetail.vehicleTypeRequired}</Descriptions.Item>
            <Descriptions.Item label="Supplier">{sr.rentalDetail.supplier?.name ?? "—"}</Descriptions.Item>
            <Descriptions.Item label="Estimated cost">{sr.rentalDetail.estimatedCostAmount / 100} {sr.rentalDetail.currency}</Descriptions.Item>
            <Descriptions.Item label="Charging period">{sr.rentalDetail.chargingPeriod || "—"}</Descriptions.Item>
            <Descriptions.Item label="Quotation ref">{sr.rentalDetail.quotationRef || "—"}</Descriptions.Item>
            <Descriptions.Item label="Actual cost">{sr.rentalDetail.actualCostAmount != null ? sr.rentalDetail.actualCostAmount / 100 : "—"}</Descriptions.Item>
          </Descriptions>
        </Card>
      )}

      {docType && <ApprovalTimeline docType={docType} docId={sr.id} refreshKey={sr.status} onActed={load} />}

      <Modal title="Cancel service request" open={cancelOpen} onCancel={() => setCancelOpen(false)} onOk={submitCancel} confirmLoading={loading}>
        <Input.TextArea rows={3} placeholder="Reason (required)" value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} />
      </Modal>

      <Modal title="Assign" open={assignOpen} onCancel={() => setAssignOpen(false)} onOk={submitAssign} confirmLoading={loading} destroyOnHidden>
        <Form form={assignForm} layout="vertical">
          {sr.type === "VEHICLE" && (
            <>
              <Form.Item name="assignedVehicleId" label="Vehicle" rules={[{ required: true }]}>
                <Select showSearch optionFilterProp="label" options={vehicles.map((v) => ({ value: v.id, label: v.registrationNo }))} />
              </Form.Item>
              <Form.Item name="driverName" label="Driver name" rules={[{ required: true }]}>
                <Input />
              </Form.Item>
              <Form.Item name="driverContact" label="Driver contact">
                <Input />
              </Form.Item>
            </>
          )}
        </Form>
      </Modal>

      <Modal title="Complete" open={completeOpen} onCancel={() => setCompleteOpen(false)} onOk={submitComplete} confirmLoading={loading} destroyOnHidden>
        <Form form={completeForm} layout="vertical">
          {sr.type === "EQUIPMENT_TRANSFER" && (
            <>
              <Form.Item name="movementRefType" label="Movement reference type" rules={[{ required: true }]}>
                <Select options={MOVEMENT_REF_TYPES} />
              </Form.Item>
              <Form.Item name="movementRefId" label="Movement reference id" rules={[{ required: true }]}>
                <InputNumber style={{ width: "100%" }} min={1} />
              </Form.Item>
            </>
          )}
          {sr.type === "VEHICLE" && (
            <Form.Item name="actualReturnAt" label="Actual return date/time">
              <DatePicker showTime style={{ width: "100%" }} />
            </Form.Item>
          )}
          {sr.type === "RENTAL" && (
            <>
              <Form.Item name="actualStartAt" label="Actual start date">
                <DatePicker style={{ width: "100%" }} />
              </Form.Item>
              <Form.Item name="actualEndAt" label="Actual end date">
                <DatePicker style={{ width: "100%" }} />
              </Form.Item>
              <Form.Item name="actualCostAmount" label="Actual cost (laari)">
                <InputNumber style={{ width: "100%" }} min={0} />
              </Form.Item>
            </>
          )}
          <Form.Item name="completionNotes" label="Completion notes">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
